// @flow

/**
 * AI Reports Chat Integration Component
 * Wraps the generic PromptChatProvider with AI Reports-specific configuration
 */

import { useCallback, useRef, useMemo, useEffect } from 'react';
import { PromptChatProvider } from '../../ai-prompt-management';
import { createThrottledTextDispatcher } from '../../ai-prompt-management/utils/throttledDispatch';
import { startChatStream } from './services/chatStreamService';
import { useFeedbackServiceWithMetadata } from '../../ai-prompt-management/context';
import {
  buildReportDesignerCreateIntroText,
  buildReportDesignerEditIntroText,
} from '../../../utils/ai-chat-questions';

type AIReportsChatWrapperProps = {
  children: React$Node,
  mode: 'create' | 'edit',
  reportId?: string,
  onReportValidated?: (data: any) => void,
  onError?: (error: { type: string, message: string }) => void,
  onSessionIdChange?: (id: string) => void,
  sessionId?: string,
  staffDetectionDisabled?: boolean,
  demographicsDisabled?: boolean,
  availableMetricGroups?: string[],
};

const AIReportsChatWrapper = ({
  children,
  mode,
  reportId,
  onReportValidated,
  onError,
  onSessionIdChange,
  sessionId: sessionIdProp,
  staffDetectionDisabled = false,
  demographicsDisabled = false,
  availableMetricGroups = [],
}: AIReportsChatWrapperProps) => {
  const feedbackService = useFeedbackServiceWithMetadata();

  const filterOptions = useMemo(
    () => ({ staffDetectionDisabled, demographicsDisabled, availableMetricGroups }),
    [staffDetectionDisabled, demographicsDisabled, availableMetricGroups]
  );

  const initialAgentText = useMemo(
    () => mode === 'edit'
      ? buildReportDesignerEditIntroText(filterOptions)
      : buildReportDesignerCreateIntroText(filterOptions),
    [mode, filterOptions]
  );
  const sessionIdRef = useRef(sessionIdProp);
  
  // Refs for streaming adapter - must be at top level
  const streamCancelRef = useRef<?Function>(null);
  const isStreamingRef = useRef<boolean>(false);
  const throttlerCleanupRef = useRef<?Function>(null);
  
  // Update ref when sessionId changes
  if (sessionIdProp && sessionIdProp !== sessionIdRef.current) {
    sessionIdRef.current = sessionIdProp;
  }

  // Create the streaming adapter factory
  const streamingAdapter = useCallback((dispatch) => {
    const { dispatch: throttledDispatch, flush, cleanup } = createThrottledTextDispatcher(dispatch);
    throttlerCleanupRef.current = cleanup;
    
    const send = async (prompt: string, agentMessageId: string) => {
      isStreamingRef.current = true;
      
      // Track last event text for duplicate detection
      let lastEventText = 'Thinking';
      
      try {
        if (streamCancelRef.current) {
          streamCancelRef.current();
          streamCancelRef.current = null;
        }

        const { cancel } = await startChatStream({
          sessionId: sessionIdRef.current || '',
          prompt,
          reportId: mode === 'edit' ? reportId : undefined,
          onEvent: (status, data) => {
            if (status === 'event') {
              // Lifecycle event (tool start/stop/error) vs legacy string event
              if (typeof data === 'object' && data !== null && data.phase) {
                if (data.phase === 'start') {
                  const ev = {
                    id: `ev-${Date.now()}-${Math.random().toString(36).slice(2)}`,
                    eventId: data.eventId,
                    text: data.label || '',
                    reason: typeof data.reason === 'string' ? data.reason : undefined,
                    withDots: true,
                    startAt: Date.now(),
                  };
                  throttledDispatch({ type: 'APPEND_EVENT', agentMessageId, event: ev });
                  lastEventText = data.label || '';
                } else if (data.phase === 'stop') {
                  if (!data.eventId) { console.warn('Received stop event without eventId'); }
                  throttledDispatch({ type: 'FINALIZE_EVENT_BY_ID', agentMessageId, eventId: data.eventId });
                } else if (data.phase === 'error') {
                  if (!data.eventId) { console.warn('Received error event without eventId'); }
                  throttledDispatch({ type: 'FINALIZE_EVENT_BY_ID', agentMessageId, eventId: data.eventId, opts: { error: true } });
                }
              } else {
                // Legacy string event
                const eventLabel = typeof data === 'string' ? data : String(data || '');
                if (eventLabel.toLowerCase() === 'thinking' && lastEventText.toLowerCase() === 'thinking') {
                  return; // Skip duplicate
                }
                const ev = {
                  id: `ev-${Date.now()}-${Math.random().toString(36).slice(2)}`,
                  text: eventLabel,
                  withDots: true,
                  startAt: Date.now(),
                };
                throttledDispatch({ type: 'APPEND_EVENT', agentMessageId, event: ev });
                lastEventText = eventLabel;
              }
            } else if (status === 'report-validated') {
              throttledDispatch({ type: 'FINALIZE_LAST_EVENT', agentMessageId });
              if (typeof onReportValidated === 'function' && data) {
                onReportValidated(data);
              }
            } else if (status === 'trace-id') {
              const traceId = data && data.traceId;
              if (traceId) {
                throttledDispatch({ type: 'SET_TRACE_ID', agentMessageId, traceId, sessionId: sessionIdRef.current });
              }
            } else if (status === 'agent-message') {
              const msgData = data && typeof data === 'object' ? data.message : data;
              const tokenStr = typeof msgData === 'string' ? msgData : String(msgData || '');
              throttledDispatch({ type: 'APPEND_AGENT_TEXT', agentMessageId, textChunk: tokenStr });
            }
          },
          onError: (message) => {
            flush();
            const errorMessage = String(message || 'An error occurred');
            throttledDispatch({ type: 'FINALIZE_LAST_EVENT', agentMessageId, opts: { error: true } });
            throttledDispatch({ type: 'SET_ERROR', error: { type: 'generic', message: errorMessage } });
            throttledDispatch({ type: 'END_STREAM' });
            isStreamingRef.current = false;
          },
          onClose: () => {
            flush();
            throttledDispatch({ type: 'FINALIZE_LAST_EVENT', agentMessageId });
            throttledDispatch({ type: 'END_STREAM' });
            isStreamingRef.current = false;
          },
        });

        streamCancelRef.current = cancel;
      } catch (err) {
        dispatch({ type: 'SET_ERROR', error: { type: 'generic', message: err.message || 'Error starting stream' } });
        dispatch({ type: 'END_STREAM' });
        isStreamingRef.current = false;
      }
    };

    const cancel = () => {
      if (streamCancelRef.current) {
        streamCancelRef.current();
        streamCancelRef.current = null;
      }
      isStreamingRef.current = false;
    };

    const reset = () => {
      if (streamCancelRef.current) {
        streamCancelRef.current();
        streamCancelRef.current = null;
      }
      isStreamingRef.current = false;
    };

    return {
      send,
      cancel,
      reset,
      get isStreaming() { return isStreamingRef.current; },
    };
  }, [mode, reportId, onReportValidated]);

  useEffect(() => () => {
    if (streamCancelRef.current) {
      streamCancelRef.current();
      streamCancelRef.current = null;
    }
    if (throttlerCleanupRef.current) {
      throttlerCleanupRef.current();
      throttlerCleanupRef.current = null;
    }
  }, []);

  return (
    <PromptChatProvider
      sessionId={sessionIdProp}
      initialAgentText={initialAgentText}
      streamingAdapter={streamingAdapter}
      feedbackService={feedbackService}
      onError={onError}
      onSessionIdChange={onSessionIdChange}
    >
      {children}
    </PromptChatProvider>
  );
};

export default AIReportsChatWrapper;
